import {
  AttachmentBlockModel,
  BookmarkBlockModel,
  CodeBlockModel,
  DatabaseBlockModel,
  ImageBlockModel,
  SurfaceRefBlockModel,
} from '@blocksuite/affine-model';
import { getSelectionRectsCommand } from '@blocksuite/affine-shared/commands';
import { EMBED_BLOCK_MODEL_LIST } from '@blocksuite/affine-shared/consts';
import {
  agentFollowing$,
  DocModeProvider,
  UserAvatarCacheProvider,
} from '@blocksuite/affine-shared/services';
import {
  agentPointerViewElement,
  isExternalGuestPresenceUser,
  isStreamLainMotionEnabled,
  matchModels,
} from '@blocksuite/affine-shared/utils';
import {
  BlockSelection,
  SurfaceSelection,
  TextSelection,
  WidgetComponent,
} from '@blocksuite/std';
import { GfxControllerIdentifier } from '@blocksuite/std/gfx';
import {
  type AgentPointer,
  type BaseSelection,
  readPresenceUser,
  type UserInfo,
} from '@blocksuite/store';
import { computed, effect } from '@preact/signals-core';
import { css, html, nothing } from 'lit';
import { state } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';
import throttle from 'lodash-es/throttle';
import { filter } from 'rxjs';

import { AgentDatabaseReveal } from '../agent-database-reveal';
import {
  AgentPointerDisplay,
  createAgentPointerOwnerResolver,
} from '../agent-pointer-display';
import { RemoteColorManager } from '../manager/remote-color-manager';
import type { DocRemoteSelectionConfig } from './config';
import {
  cursorLabelContent,
  cursorLabelStyle,
  cursorStyle,
  REMOTE_CURSOR_TRANSIENT_GRACE_MS,
  resolveStableRemoteCursorRect,
  resolveTableSelectionCaretRange,
  selectionStyle,
} from './utils';

export interface SelectionRect {
  width: number;
  height: number;
  top: number;
  left: number;
  transparent?: boolean;
}

export const AFFINE_DOC_REMOTE_SELECTION_WIDGET =
  'affine-doc-remote-selection-widget';

const stableCursorRectsByStore = new WeakMap<
  object,
  Map<number, { rect: SelectionRect; updatedAt: number }>
>();
const stableSelectionsByStore = new WeakMap<
  object,
  Map<
    number,
    {
      selections: BaseSelection[];
      user?: UserInfo;
    }
  >
>();

export class AffineDocRemoteSelectionWidget extends WidgetComponent {
  private _agentPointerDisplay: AgentPointerDisplay | null = null;
  // avoid being unable to select text by mouse click or drag
  static override styles = css`
    :host {
      pointer-events: none;
    }
  `;

  @state()
  private accessor _selections: Array<{
    id: number;
    selections: BaseSelection[];
    rects: SelectionRect[];
    user?: UserInfo;
  }> = [];

  private readonly _abortController = new AbortController();

  private _remoteColorManager: RemoteColorManager | null = null;

  private get _stableCursorRects() {
    let stableCursorRects = stableCursorRectsByStore.get(this.store);
    if (!stableCursorRects) {
      stableCursorRects = new Map();
      stableCursorRectsByStore.set(this.store, stableCursorRects);
    }
    return stableCursorRects;
  }

  private get _stableSelections() {
    let stableSelections = stableSelectionsByStore.get(this.store);
    if (!stableSelections) {
      stableSelections = new Map();
      stableSelectionsByStore.set(this.store, stableSelections);
    }
    return stableSelections;
  }

  private readonly _stableCursorExpiryTimers = new Map<
    number,
    ReturnType<typeof setTimeout>
  >();

  private readonly _remoteSelections = computed(() => {
    const status = this.store.awarenessStore.getStates();
    return [...this.std.selection.remoteSelections.entries()].map(
      ([id, selections]) => {
        return {
          id,
          selections,
          user: readPresenceUser(status, id),
        };
      }
    );
  });

  private readonly _resizeObserver: ResizeObserver = new ResizeObserver(() => {
    this._agentPointerDisplay?.update();
    this.requestUpdate();
  });

  private get _config(): DocRemoteSelectionConfig {
    return {
      blockSelectionBackgroundTransparent: block => {
        return matchModels(block, [
          CodeBlockModel,
          DatabaseBlockModel,
          ImageBlockModel,
          AttachmentBlockModel,
          BookmarkBlockModel,
          SurfaceRefBlockModel,
          ...EMBED_BLOCK_MODEL_LIST,
        ]);
      },
    };
  }

  private get _container() {
    return this.offsetParent;
  }

  private get _containerRect() {
    return this.offsetParent?.getBoundingClientRect();
  }

  private get _selectionManager() {
    return this.host.selection;
  }

  private _getTextRange(textSelection: TextSelection): Range | null {
    const toBlockId = textSelection.to
      ? textSelection.to.blockId
      : textSelection.from.blockId;

    let range = this.std.range.textSelectionToRange(
      this._selectionManager.create(TextSelection, {
        from: {
          blockId: toBlockId,
          index: textSelection.to
            ? textSelection.to.index + textSelection.to.length
            : textSelection.from.index + textSelection.from.length,
          length: 0,
        },
        to: null,
      })
    );

    if (!range) {
      // If no range, maybe the block is not updated yet
      // We just set the range to the end of the block
      const block = this.std.view.getBlock(toBlockId);
      if (!block) return null;

      range = this.std.range.textSelectionToRange(
        this._selectionManager.create(TextSelection, {
          from: {
            blockId: toBlockId,
            index: block.model.text?.length ?? 0,
            length: 0,
          },
          to: null,
        })
      );

      if (!range) return null;
    }

    return range;
  }

  private _getCursorRect(selections: BaseSelection[]): SelectionRect | null {
    if (!this.block) {
      return null;
    }

    if (this.block.model.flavour !== 'affine:page') {
      console.error('remote selection widget must be used in page component');
      return null;
    }

    const textSelection = selections.find(
      selection => selection instanceof TextSelection
    ) as TextSelection | undefined;
    const blockSelections = selections.filter(
      selection => selection instanceof BlockSelection
    );
    const container = this._container;
    const containerRect = this._containerRect;

    const tableRange = resolveTableSelectionCaretRange(selections, blockId =>
      this.host.view.getBlock(blockId)
    );

    if (textSelection || tableRange) {
      const range = tableRange ?? this._getTextRange(textSelection!);
      if (!range) return null;

      const container = this._container;
      const containerRect = this._containerRect;
      const rangeRects = Array.from(range.getClientRects());
      if (rangeRects.length > 0) {
        const rect =
          rangeRects.length === 1
            ? rangeRects[0]
            : rangeRects[rangeRects.length - 1];
        return {
          width: 2,
          height: rect.height,
          top:
            rect.top - (containerRect?.top ?? 0) + (container?.scrollTop ?? 0),
          left:
            rect.left -
            (containerRect?.left ?? 0) +
            (container?.scrollLeft ?? 0),
        };
      }
    } else if (blockSelections.length > 0) {
      const lastBlockSelection = blockSelections[blockSelections.length - 1];

      const block = this.host.view.getBlock(lastBlockSelection.blockId);
      if (block) {
        const rect = block.getBoundingClientRect();

        return {
          width: 2,
          height: rect.height,
          top:
            rect.top - (containerRect?.top ?? 0) + (container?.scrollTop ?? 0),
          left:
            rect.left +
            rect.width -
            (containerRect?.left ?? 0) +
            (container?.scrollLeft ?? 0),
        };
      }
    }

    return null;
  }

  private readonly _getSelectionRect = (
    selections: BaseSelection[]
  ): SelectionRect[] => {
    if (!this.block) {
      return [];
    }

    if (this.block.model.flavour !== 'affine:page') {
      console.error('remote selection widget must be used in page component');
      return [];
    }

    const textSelection = selections.find(
      selection => selection instanceof TextSelection
    ) as TextSelection | undefined;
    const blockSelections = selections.filter(
      selection => selection instanceof BlockSelection
    );

    if (!textSelection && !blockSelections.length) return [];

    const [_, { selectionRects }] = this.std.command.exec(
      getSelectionRectsCommand,
      {
        textSelection,
        blockSelections,
      }
    );

    if (!selectionRects) return [];

    return selectionRects.map(({ blockId, ...rect }) => {
      if (!blockId) return rect;

      const block = this.host.view.getBlock(blockId);
      if (!block) return rect;

      const isTransparent = this._config.blockSelectionBackgroundTransparent(
        block.model
      );

      return {
        ...rect,
        transparent: isTransparent,
      };
    });
  };

  override connectedCallback() {
    super.connectedCallback();

    const view = this.ownerDocument.defaultView!;
    // On the canvas its widget shows the crewmate; one pointer, one carry.
    const pageMode = () =>
      this.std.getOptional(DocModeProvider)?.getEditorMode() !== 'edgeless';
    const noPointers: ReadonlyMap<string, AgentPointer> = new Map();
    // A database row whose change moved it is carried there by the pointer.
    const databaseReveal = new AgentDatabaseReveal({
      playback: this.std.selection.slots.agentPlayback.pipe(filter(pageMode)),
      block: id => this.std.view.getBlock(id),
      view,
      reduced: () =>
        !isStreamLainMotionEnabled() ||
        view.matchMedia('(prefers-reduced-motion: reduce)').matches,
      drawn: () => agentPointers.update(),
      following: () => agentFollowing$.value,
    });
    this.disposables.add(() => databaseReveal.dispose());
    const agentPointers = new AgentPointerDisplay({
      pointers: () => {
        const pointers = this.std.selection.agentPointers;
        return pageMode() ? pointers : noPointers;
      },
      carry: target => databaseReveal.carry(target),
      owner: createAgentPointerOwnerResolver(this.std),
      position: target => {
        const block = this.std.view.getBlock(target.id);
        if (!block) return null;
        // A header control of the view, a database row or cell: where the
        // open view shows it; any other target stands at the block.
        const shown = agentPointerViewElement(block, target);
        const rect = databaseReveal.placeOf(
          target,
          shown === undefined ? block : shown
        );
        if (!rect || rect.width === 0 || rect.height === 0) return null;
        const container = this._container;
        const containerRect = this._containerRect;
        return {
          x:
            rect.left -
            (containerRect?.left ?? 0) +
            (container?.scrollLeft ?? 0) +
            Math.min(12, rect.width / 2),
          y:
            rect.top -
            (containerRect?.top ?? 0) +
            (container?.scrollTop ?? 0) +
            Math.min(12, rect.height / 2),
        };
      },
      scale: () => 1,
      changed: () => this.requestUpdate(),
      view: this.ownerDocument.defaultView!,
      avatarCache: this.std.getOptional(UserAvatarCacheProvider) ?? null,
    });
    this._agentPointerDisplay = agentPointers;
    this.disposables.add(effect(agentPointers.update));
    this.disposables.add(() => {
      agentPointers.dispose();
      this._agentPointerDisplay = null;
    });
    this.disposables.addFromEvent(
      this.ownerDocument,
      'scroll',
      agentPointers.update,
      true
    );

    this.handleEvent('wheel', () => {
      agentPointers.update();
      this.requestUpdate();
    });

    this.disposables.addFromEvent(window, 'resize', () => {
      agentPointers.update();
      this.requestUpdate();
    });

    this._remoteColorManager = new RemoteColorManager(this.std);

    this.disposables.add(
      effect(() => {
        const selections = this._remoteSelections.value;
        this._updateSelectionsThrottled(selections);
      })
    );
    this.disposables.add(
      this.std.store.slots.blockUpdated.subscribe(() => {
        agentPointers.update();
        this._updateSelectionsThrottled(this._remoteSelections.peek());
      })
    );
    const gfx = this.std.get(GfxControllerIdentifier);
    this.disposables.add(
      gfx.viewport.viewportUpdated.subscribe(() => {
        agentPointers.update();
        const selections = this._remoteSelections.peek();
        this._updateSelections(selections);
      })
    );
    this.disposables.add(
      this.std.event.active$.subscribe(value => {
        if (!value) {
          this.std.selection.clearRemote(['block', 'cursor', 'table', 'text']);
        }
      })
    );
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this._resizeObserver.disconnect();
    this._abortController.abort();
    for (const timer of this._stableCursorExpiryTimers.values()) {
      clearTimeout(timer);
    }
    this._stableCursorExpiryTimers.clear();
  }

  private readonly _updateSelections = (
    selections: typeof this._remoteSelections.value
  ) => {
    const now = performance.now();
    const completeSelections = [...selections];
    const incomingIds = new Set(selections.map(selection => selection.id));
    for (const [id, previous] of this._stableSelections) {
      const stableCursor = this._stableCursorRects.get(id);
      if (
        !incomingIds.has(id) &&
        stableCursor &&
        now - stableCursor.updatedAt <= REMOTE_CURSOR_TRANSIENT_GRACE_MS
      ) {
        completeSelections.push({
          id,
          selections: previous.selections,
          user: previous.user,
        });
      }
    }
    const remoteUsers = new Set<number>();
    const activeRemoteUsers = new Set(
      completeSelections.map(selection => selection.id)
    );
    for (const id of this._stableCursorRects.keys()) {
      if (!activeRemoteUsers.has(id)) {
        this._stableCursorRects.delete(id);
        this._stableSelections.delete(id);
        const timer = this._stableCursorExpiryTimers.get(id);
        if (timer) clearTimeout(timer);
        this._stableCursorExpiryTimers.delete(id);
      }
    }
    this._selections = completeSelections.flatMap(
      ({ selections, id, user }) => {
        if (remoteUsers.has(id)) {
          return [];
        } else {
          remoteUsers.add(id);
        }
        if (selections.length > 0) {
          this._stableSelections.set(id, { selections, user });
        }

        return {
          id,
          selections,
          rects: this._getSelectionRect(selections),
          user,
        };
      }
    );
  };

  private readonly _updateSelectionsThrottled = throttle(
    this._updateSelections,
    60
  );

  private _getStableCursorRect(
    id: number,
    selections: BaseSelection[]
  ): SelectionRect | null {
    const now = performance.now();
    const current = this._getCursorRect(selections);
    const previous = this._stableCursorRects.get(id);
    const stable = resolveStableRemoteCursorRect({
      current,
      editingSurfaceActive: selections.some(
        selection => selection instanceof SurfaceSelection && selection.editing
      ),
      previous: previous?.rect ?? null,
      previousAgeMs: previous ? now - previous.updatedAt : undefined,
      textSelectionActive: selections.some(
        selection => selection instanceof TextSelection
      ),
    });

    if (current) {
      this._stableCursorRects.set(id, { rect: current, updatedAt: now });
      const timer = this._stableCursorExpiryTimers.get(id);
      if (timer) clearTimeout(timer);
      this._stableCursorExpiryTimers.delete(id);
    } else if (stable && previous) {
      if (!this._stableCursorExpiryTimers.has(id)) {
        const remainingMs = Math.max(
          0,
          REMOTE_CURSOR_TRANSIENT_GRACE_MS - (now - previous.updatedAt)
        );
        this._stableCursorExpiryTimers.set(
          id,
          setTimeout(() => {
            this._stableCursorExpiryTimers.delete(id);
            this.requestUpdate();
          }, remainingMs + 1)
        );
      }
    } else {
      this._stableCursorRects.delete(id);
      const timer = this._stableCursorExpiryTimers.get(id);
      if (timer) clearTimeout(timer);
      this._stableCursorExpiryTimers.delete(id);
    }
    return stable;
  }

  override render() {
    if (
      this._selections.length === 0 &&
      this.std.selection.agentPointers.size === 0
    ) {
      return nothing;
    }

    const remoteColorManager = this._remoteColorManager;
    if (!remoteColorManager) return nothing;
    return html`<div>
      ${this._agentPointerDisplay?.render()}
      ${this._selections.map(selection => {
        const color = remoteColorManager.get(selection.id);
        if (!color) return [];
        const cursorRect = this._getStableCursorRect(
          selection.id,
          selection.selections
        );

        return selection.rects
          .map(r => html`<div style="${selectionStyle(r, color)}"></div>`)
          .concat([
            html`
              <div
                data-testid="remote-doc-cursor"
                style="${cursorRect
                  ? cursorStyle(cursorRect, color)
                  : styleMap({
                      display: 'none',
                    })}"
              >
                <div
                  style="${styleMap({
                    position: 'relative',
                    height: '100%',
                  })}"
                >
                  <div
                    style="${selection.user
                      ? cursorLabelStyle(
                          cursorRect?.height ?? 0,
                          color,
                          isExternalGuestPresenceUser(selection.user)
                        )
                      : styleMap({ display: 'none' })}"
                  >
                    ${selection.user
                      ? cursorLabelContent(selection.user, color)
                      : nothing}
                  </div>
                </div>
              </div>
            `,
          ]);
      })}
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [AFFINE_DOC_REMOTE_SELECTION_WIDGET]: AffineDocRemoteSelectionWidget;
  }
}
